/** @jsx jsx */
import { jsx } from '@emotion/core';
import classnames from 'classnames/bind';
import { useState } from 'react';
import serverRenderingUtils from '../../../helpers/serverRenderingUtils';
import { Icon } from '../../shared/Icon';
import Text from '../../shared/Text';
import styles from './styles.sass';
import {
  FieldInputPresentationComponentProps,
  FieldInputProps,
  FieldInputWithReduxProps,
} from './types';

const cx = classnames.bind(styles);

const isMobile =
  serverRenderingUtils.isIOS() || serverRenderingUtils.isAndroidChrome();

const FieldInputPresentationComponent = ({
  name,
  type,
  onChange,
  placeholder,
  value,
  minNumberValue,
  autofocus,
  maxCharacterLength,
  isShowingError,
  error,
  iconType,
  onClear,
  cssProp,
}: FieldInputPresentationComponentProps) => {
  const isShowingBottomSection = maxCharacterLength || isShowingError;
  const showClearButton = !!onClear && value !== '';
  return (
    <div css={cssProp}>
      <div className={styles.inputContainer}>
        {iconType && (
          <div className={styles.iconWrapper}>
            <Icon type={iconType} fillColor="#dedfe0" />
          </div>
        )}
        <input
          className={cx({
            input: true,
            numberInput: type === 'number',
            numberInputMobile: isMobile,
            inputError: isShowingError,
            inputWithClearButton: onClear,
            inputWithIcon: iconType,
          })}
          name={name}
          id={name}
          onChange={onChange}
          placeholder={placeholder}
          value={value}
          type={type}
          min={type === 'number' ? minNumberValue : undefined}
          autoFocus={autofocus}
        />
        {showClearButton && (
          <button
            type="button"
            onClick={onClear}
            name="Clear input"
            className={styles.clearButton}
          >
            <Icon type="x" fillColor="#fff" />
          </button>
        )}
      </div>
      {isShowingBottomSection && (
        <div className={styles.bottomSection}>
          <div
            className={cx({
              errorText: true,
              errorTextVisible: isShowingError,
            })}
          >
            <Text color="#d0021b">{error}</Text>
          </div>
          {maxCharacterLength && (
            <Text size="sm" color="#7f8287">
              {`${
                value === undefined ? 0 : value.length
              } / ${maxCharacterLength}`}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

const FieldInput = ({
  name,
  type,
  onChange,
  placeholder,
  defaultValue,
  minNumberValue,
  autofocus,
  maxCharacterLength,
  error,
  iconType,
  onClear,
  isShowingError,
  cssProp,
}: FieldInputProps) => {
  const [value, setValue] = useState(defaultValue);
  return (
    <FieldInputPresentationComponent
      cssProp={cssProp}
      name={name}
      type={type}
      onChange={e => {
        setValue(e.currentTarget.value);
        onChange(e);
      }}
      placeholder={placeholder}
      value={value}
      minNumberValue={minNumberValue}
      autofocus={autofocus}
      maxCharacterLength={maxCharacterLength}
      isShowingError={isShowingError}
      error={error}
      iconType={iconType}
      onClear={
        onClear !== undefined
          ? () => {
              setValue('');
              onClear();
            }
          : undefined
      }
    />
  );
};

const FieldInputWithRedux = ({
  input: { name, onChange, value },
  meta: { error, valid, submitFailed },
  type,
  placeholder,
  maxCharacterLength,
  minNumberValue,
  autofocus,
  cssProp,
}: FieldInputWithReduxProps) => (
  <FieldInputPresentationComponent
    cssProp={cssProp}
    name={name}
    type={type}
    onChange={onChange}
    placeholder={placeholder}
    value={value}
    minNumberValue={minNumberValue}
    autofocus={autofocus}
    maxCharacterLength={maxCharacterLength}
    isShowingError={submitFailed && !valid}
    error={error}
  />
);

export { FieldInput, FieldInputWithRedux };
